import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { defaults } from '@/helpers/appDefaults';
import { kgToPound } from '@/helpers/convert';
import { LENGTH_UNITS } from '@/modules/Flexigo/helpers/constants';
import store from '@/modules/Flexigo/store/store';

export function objectifyByKey(arr, key) {
  if (!Array.isArray(arr)) throw new Error('not an array');
  if (!arr.length) return;

  const reducer = (acc, el) => {
    if (el[key]) {
      return { ...acc, [el[key]]: el };
    }
    return acc;
  };

  /* eslint-disable-next-line */
  return arr.reduce(reducer, {});
}

/* eslint-disable no-bitwise */
export function hashCode(str) {
  let h = 0xdeadbeef;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 2654435761);
  }
  return (h ^ (h >>> 16)) >>> 0; // eslint-disable-line
}
/* eslint-enable no-bitwise */

export function flattenHierarchy(hierarchy, childrenKey = 'children') {
  const result = [];
  result.push(hierarchy);
  hierarchy[childrenKey].forEach((childNode) => {
    const flattenedChild = flattenHierarchy(childNode);
    flattenedChild.forEach((item) => result.push(item));
  });
  return result;
}

export function verifyVkn(vkn) {
  if (typeof vkn !== 'string') throw new TypeError('vkn should be a string');
  if (vkn.length !== 10) throw new TypeError('invalid length');

  const digits = vkn.split('');
  const control = digits.pop(); // eslint-disable-line

  const r = [];

  digits.forEach((el, i) => {
    const x = Number(el) + 10 - (i + 1);
    const mod = x % 10;
    if (mod === 9) {
      r.push(mod);
    } else {
      const pow = 2 ** (10 - (i + 1));
      r.push((mod * pow) % 9);
    }
  });

  const total = r.reduce((acc, cur) => acc + cur);
  const c = (10 - ((total % 10) % 10)) % 10;

  return `${digits.join('')}${c}` === vkn;
}

export function guessCardType(cc) {
  const number = cc;
  let re = new RegExp('^4');
  if (number.match(re) != null) return 'visa';

  re = new RegExp('^(34|37)');
  if (number.match(re) != null) return 'amex';

  re = new RegExp('^5[1-5]');
  if (number.match(re) != null) return 'mastercard';

  re = new RegExp('^6011');
  if (number.match(re) != null) return 'discover';

  return 'other'; // default type
}

/* eslint-disable no-bitwise */
export function hexToRgb(hex) {
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return [r, g, b].join();
}
/* eslint-enable no-bitwise */

export function getRandomHexColor(times) {
  const colors = [];
  for (let i = 0; i < times; i++) {
    colors.push(`#${`000000${Math.random().toString(16).slice(2, 8).toUpperCase()}`.slice(-6)}`); // eslint-disable-line
  }
  return colors;
}

// Shade a hex color by `amount` in the range [-1, 1]. Negative darkens, positive lightens.
// Example: shadeHex('#4285F4', -0.2) => 20% darker, shadeHex('#4285F4', 0.2) => 20% lighter
export function shadeHex(hex, amount) {
  if (!hex || typeof hex !== 'string') return hex;
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);
  const factor = 1 + amount;
  const toHex = (v) =>
    clamp(v * factor)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function chunkArray(arr, chunkSize) {
  const r = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    r.push(arr.slice(i, i + chunkSize));
  }

  return r;
}

export function throttle(callback, wait, immediate = false) {
  let timeout = null;
  let initialCall = true;

  return () => {
    const callNow = immediate && initialCall;
    const next = (...args) => {
      callback.apply(this, ...args);
      timeout = null;
    };

    if (callNow) {
      initialCall = false;
      next();
    }

    if (!timeout) {
      timeout = setTimeout(next, wait);
    }
  };
}

export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function generateRandomHtmlId(length = 8) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'; // Letters to choose from
  const digits = '0123456789'; // Digits to choose from
  let randomId = '';

  randomId += characters.charAt(Math.floor(Math.random() * characters.length)); // Start with a letter

  for (let i = 1; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * digits.length);
    randomId += digits.charAt(randomIndex);
  }

  return randomId;
}

export function createUniqueToken() {
  let d = new Date().getTime();
  const uuid = 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/[x]/g, (c) => {
    const r = (d + Math.random() * 16) % 16 | 0; // eslint-disable-line
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x7) | 0x8).toString(16); // eslint-disable-line
  });
  return uuid;
}

export function generateFastRandomString(length, alphanumericOnly = true) {
  const alphaChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  const numChars = '0123456789';
  const characters = alphanumericOnly ? alphaChars + numChars : alphaChars;
  const charactersLength = characters.length;
  const result = new Array(length);

  for (let i = 0; i < length; i++) {
    result[i] = characters[(Math.random() * charactersLength) | 0]; // eslint-disable-line
  }

  return result.join('');
}

export function createFileFromBinary(data, type, fileName) {
  const fileAnchor = document.createElement('a');
  const url = new Blob([data], { type });

  document.body.appendChild(fileAnchor).style = 'display: none';
  fileAnchor.className = 'download-file-temp-link';
  fileAnchor.href = URL.createObjectURL(url);
  fileAnchor.download = fileName;
  fileAnchor.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(fileAnchor);
}

export function snakeToCamel(str) {
  return str.toLowerCase().replace(/([-_][a-z0-9])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
}

export function isMac() {
  return navigator.userAgent.indexOf('Mac') > -1;
}

// Host apps built on the Kurogo platform (e.g. UHGO) embed the rider pages in a
// webview and provide their own navigation chrome. Not every Kurogo webview
// advertises "Kurogo" in its user agent (Android), so the embedded URL can also
// opt in with a kurogo=true/1 query param (before or after the hash). A positive
// detection sticks for the browsing session so SSO redirects and in-app
// navigation that drop the param keep the chrome hidden.
const KUROGO_SESSION_KEY = 'flexigo.kurogoWebview';

export function isKurogoWebview() {
  try {
    if (sessionStorage.getItem(KUROGO_SESSION_KEY) === 'true') return true;
  } catch (e) {
    // sessionStorage can be unavailable in some webviews; fall back to per-load detection
  }

  const detected = /kurogo/i.test(navigator.userAgent) || /[?&]kurogo=(true|1)(?=[&#]|$)/i.test(window.location.href);
  if (detected) {
    try {
      sessionStorage.setItem(KUROGO_SESSION_KEY, 'true');
    } catch (e) {
      // ignore: detection still works for this page load
    }
  }
  return detected;
}

export function capitalizeFirstLetter(string) {
  return `${string.charAt(0).toUpperCase()}${string.slice(1)}`;
}

export function getYesNo(value) {
  return value ? 'Evet' : 'Hayır';
}

export function toggleArrayItem(array, item) {
  const i = array.findIndex((el) => el === item);
  if (i < 0) {
    array.push(item);
  } else {
    array.splice(i, 1);
  }

  return array;
}

// because vue2 cant detect changes on sets properly
export function uniqueArray(array) {
  return [...new Set(array)];
}

export function removeFromArray(array, item) {
  const i = array.findIndex((el) => el === item);
  if (i < 0) return array;
  array.splice(i, 1);
  return array;
}

export function toLatLng(array) {
  return array.map((el) => ({ lat: el.latitude, lng: el.longitude }));
}

export function toLatitudeLongitude(array) {
  return array.map((el) => ({ latitude: el.lat, longitude: el.lng }));
}

// This is mostly for iframe issues
// Not needed elsewhere
export function getLink(lang, hash, isFlexigoPath = false) {
  const url = document.referrer ? new URL(document.referrer) : new URL(window.location.href);
  url.hash = hash;
  const isProduction = !!url.origin.includes('secure.flexigo') || !!url.origin.includes('portal.flexigo');

  if (url.searchParams.has('lang')) {
    url.searchParams.set('lang', lang);
  } else {
    url.searchParams.append('lang', lang);
  }
  const formattedUrl = isFlexigoPath
    ? url.href.replace('admin/flexigo', 'flexigo').replace('commute/flexigo', 'commute')
    : url.href;

  return isProduction ? formattedUrl.replace('/flexigo', '/commute') : formattedUrl;
}

export function formatNumber(
  number,
  locale = 'en-US',
  maximumFractionDigits = 2,
  emptySign = '-',
  minimumFractionDigits = 0,
) {
  if (number === undefined || Number.isNaN(number)) return emptySign;
  // const l = locales[locale] || 'en-US';
  return new Intl.NumberFormat(locale, { maximumFractionDigits, minimumFractionDigits }).format(number);
}

export function formatNumberWithDecimal(
  number,
  locale = 'en-US',
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  emptySign = '-',
) {
  if (number === undefined || number === 'NaN') return emptySign;
  // const l = locales[locale] || 'en-US';
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(number);
}

export function getCurrencySymbol(currency, locale = navigator.language) {
  return (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim();
}

export function formatCurrency(
  number,
  currency = 'USD',
  locale = navigator.language,
  minimumFractionDigits = 2,
  emptySign = '-',
) {
  if (number === undefined || number === 'NaN') return emptySign;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
  }).format(number);
}

export function formatPercent(
  number,
  locale = 'en-US',
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
  emptySign = '-',
) {
  if (number === undefined || number === 'NaN') return emptySign;
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(number / 100);
}

export function formatDigit(
  number,
  locale = 'en-US',
  minimumFractionDigits = 0,
  maximumFractionDigits = 2,
  emptySign = '-',
) {
  if (number === null || number === undefined || Number.isNaN(number)) return emptySign;

  return number.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

// English browser locales that should get the English (US-style) formats.
const EN_LOCALES = ['en-US', 'en-GB'];

// this function checks only the browser language
// for 24h format check is24HourFormat function
//
// NOTE: the name says en-US, but the list deliberately includes en-GB as well.
// The product ships two languages, Turkish and English, and its English variant
// follows US conventions — so an English-speaking admin should get the English
// formats rather than falling through to the Turkish ones. The name is kept as
// is to avoid churning ~100 call sites.
export function isLocaleEnUs() {
  return EN_LOCALES.includes(navigator.language);
}

export function isUsDateFormat() {
  const lengthUnit = store.state.companySettings?.lengthUnit;

  return lengthUnit ? lengthUnit === LENGTH_UNITS.IMPERIAL : isLocaleEnUs();
}

export function isImperial() {
  const { lengthUnit } = store.state.companySettings || {};

  return lengthUnit ? lengthUnit === LENGTH_UNITS.IMPERIAL : isLocaleEnUs();
}

export function isLocaleTr() {
  return navigator.language === 'tr';
}

export function formatDateLocal(date) {
  return isLocaleEnUs() ? format(date, 'MMM d, yyyy') : format(date, 'd MMMM yyyy');
}

export function timeStampLocalFormatWithLocalize(time) {
  return isLocaleEnUs()
    ? format(new Date(time), defaults.textFirstMonthDate)
    : format(new Date(time), defaults.textMiddleMonthDate, { locale: tr });
}

export function getInitials(name) {
  if (!name) return '';
  const names = name.split(' ');
  const initials = names.map((n) => n[0].toUpperCase());
  return initials.join('');
}

export function displayFullNameWithSurnameInitial(fullName) {
  const nameParts = fullName.split(' ');

  if (nameParts.length < 2) {
    console.log('Invalid full name format.');
    return fullName;
  }

  const firstName = nameParts[0];
  const surnameInitial = nameParts[nameParts.length - 1][0].toUpperCase();

  return `${firstName} ${surnameInitial}.`;
}

export function getPercentage(value, total, decimals = 0) {
  if (total === 0) return 0;
  if (Number.isNaN(value) || Number.isNaN(total)) return 0;
  if (decimals > 0) return Number(((value / total) * 100).toFixed(decimals));
  return Math.round((value / total) * 100);
}

// WARNING: This function returns "0" for NaN values and undefined, use with caution or better DONT use it at all
export function decimalNumber(value, decimals = 2) {
  if (!value || Number.isNaN(value) || value === 'NaN') return 0;
  return Number(value.toFixed(decimals));
}

export async function* asyncPollingGenerator(fetchFunction, interval) {
  let isPaused = false;

  // Handle visibility change event
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      isPaused = true;
    } else {
      isPaused = false;
    }
  });

  while (true) {
    if (!isPaused) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchFunction();
        yield data;
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }

    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

// This function parses the parameters from the hash of the URL
// and returns them as an object. Normally, the parameters are in searchParams
// but in the case of a vue router, they are in the hash.
// This function does NOT handle multiple parameters with the same name or arrays.
export function parseHashParams(url) {
  // Extracting the hash and then the query part from the hash
  const { hash } = new URL(url);
  const queryString = hash.split('?')[1];

  if (!queryString) {
    return {};
  }

  // Parsing the query string
  const params = new URLSearchParams(queryString);
  const paramsObject = {};

  // Creating an object from the parsed parameters
  params.forEach((value, key) => {
    paramsObject[key] = value;
  });

  return paramsObject;
}

/**
 * Filters an object based on a predicate function.
 *
 * @param {Object} obj - The object to filter.
 * @param {Function} predicate - A predicate function that takes an object value and returns a boolean.
 *                               If it returns true, the corresponding key-value pair is included in the output object.
 * @returns {Object} A new object with key-value pairs that match the predicate function.
 *
 */
export function filterObject(obj, predicate) {
  return Object.keys(obj)
    .filter((key) => predicate(obj[key]))
    .reduce((res, key) => ({ ...res, [key]: obj[key] }), {});
}

// wrapper for filterObject to filter by simple value equality
export function filterObjectByValue(obj, value) {
  return filterObject(obj, (v) => v === value);
}

export function formatWeight(weightInKg, locale = 'en-US') {
  const isUSLocale = locale === 'en-US';
  let weight;
  let unit;

  if (isUSLocale) {
    // Convert to pounds
    const weightInPounds = kgToPound(weightInKg);
    if (weightInPounds < 2000) {
      weight = weightInPounds;
      unit = 'lb';
    } else {
      weight = weightInPounds / 2000;
      unit = 'tons';
    }
  } else {
    // Use kilograms or metric tons
    weight = weightInKg < 1000 ? weightInKg : weightInKg / 1000;
    unit = weightInKg < 1000 ? 'kg' : 'ton';
  }
  return `${weight.toFixed(2)} ${unit}`;
}

export function isElementOverflowPageContent(element) {
  const documentWidth = document.documentElement.offsetWidth;
  const box = element.getBoundingClientRect();
  return box.left < 0 || box.right > documentWidth;
}

export function getBaseUrl() {
  if (window.location.origin.includes('localhost')) return 'https://secure3.vektorteknoloji.com/commute';
  return `${window.location.origin}/commute`;
}

export function isEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }

  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }

  if (Array.isArray(a)) {
    if (a.length !== b.length) {
      return false;
    }

    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) {
        return false;
      }
    }
  } else {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) {
      return false;
    }

    // eslint-disable-next-line
    for (const key of aKeys) {
      // eslint-disable-next-line
      if (!b.hasOwnProperty(key) || !isEqual(a[key], b[key])) {
        return false;
      }
    }
  }

  return true;
}

export function isEmpty(value) {
  if (value == null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
}

export function groupBy(xs, fnBy) {
  const valueProcessor = (x) => (typeof fnBy === 'function' ? fnBy(x) : x[fnBy]);
  return xs.reduce((rv, x) => {
    // eslint-disable-next-line
    (rv[valueProcessor(x)] = rv[valueProcessor(x)] || []).push(x);
    return rv;
  }, {});
}

export const clearSearchString = (searchString) => {
  const charMap = {
    Ç: 'C',
    Ö: 'O',
    Ş: 'S',
    İ: 'I',
    I: 'I',
    Ü: 'U',
    Ğ: 'G',
    ç: 'c',
    ö: 'o',
    ş: 's',
    ı: 'i',
    ü: 'u',
    ğ: 'g',
  };
  let clearedString = '';
  searchString.split('').forEach((item) => {
    clearedString += charMap[item] || item;
  });

  return clearedString.replace(/[^\w\s]/gi, '');
};

export const stripHtml = (html) => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export function cloneObject(obj) {
  return structuredClone(obj);
}

// remove duplicate records from array list
// filterBy - filter list according to filterBy value, function
export function filterDuplicatesFromArray(arrayList, filterBy = (item) => item) {
  const duplicates = [];
  const clearedList = [];
  arrayList.forEach((item) => {
    if (!clearedList.map((cleared) => filterBy(cleared)).includes(item.id)) {
      clearedList.push(item);
    } else {
      duplicates.push(filterBy(item));
    }
  });
  return clearedList;
}

// remove duplicate records from array list
// filterBy - filter list according to filterBy value, function
export function filterDuplicates(arrayList, filterBy = (item) => item, includeDuplicates = false) {
  const duplicates = [];
  const clearedList = [];

  arrayList.forEach((item) => {
    if (!filterBy(item) || !clearedList.map((cleared) => filterBy(cleared)).includes(filterBy(item))) {
      clearedList.push(item);
    } else {
      duplicates.push(item);
    }
  });

  if (duplicates.length) {
    clearedList.forEach((item) => {
      if (duplicates.map((cleared) => filterBy(cleared)).includes(filterBy(item))) {
        duplicates.push(item);
      }
    });
  }
  return includeDuplicates ? { list: clearedList, duplicates } : clearedList;
}

export function isMobile() {
  return window.innerWidth < 700;
}

export function isPhoneValid(phoneNumber, local = { shortCode: 'TR', name: 'Turkey', areaCode: '90' }) {
  // For other countries we can't know the minimum number of digits, so we just
  // check if there is atleast 1 digit
  if (!phoneNumber || phoneNumber.length < 1) return false;
  let regex = /^[^0](\d ?){1,}$/;
  if (local.areaCode === '90') regex = /^[^0](\d ?){9,10}$/; // Allow 9 or 10 digits for Turkish numbers
  return regex.test(phoneNumber);
}

/**
 * Checks if a value is semantically null/empty
 * Handles: null, undefined, empty objects with all null properties
 * @param {*} value - Value to check
 * @returns {boolean} - True if semantically null
 */
export function isSemanticNull(value) {
  if (value === null || value === undefined) return true;

  if (typeof value !== 'object' || Array.isArray(value)) return false;

  // Check if object has all null/undefined properties (ignoring specific flags)
  const meaningfulKeys = Object.keys(value).filter((k) => !['notValid', 'timestamp'].includes(k));
  return meaningfulKeys.every((key) => value[key] === null || value[key] === undefined);
}

/**
 * Compares two objects and returns detailed change information
 * @param {Object} oldObj - Original object (default values)
 * @param {Object} newObj - Current object (form values)
 * @param {Object} options - Configuration options
 * @param {boolean} options.deepCompare - Use deep comparison (default: true)
 * @param {boolean} options.includeUnchanged - Include unchanged fields (default: false)
 * @param {Array} options.excludeKeys - Keys to exclude from comparison (default: [])
 * @param {Function} options.normalizeValue - Custom normalizer function (default: null)
 * @param {boolean} options.treatSemanticNullAsEqual - Treat semantic nulls as equal (default: true)
 * @returns {Object} - Changes object with structure: { fieldName: { old: value, new: value } }
 */
export function getObjectChanges(oldObj, newObj, options = {}) {
  const {
    deepCompare = true,
    includeUnchanged = false,
    excludeKeys = [],
    normalizeValue = null,
    treatSemanticNullAsEqual = true,
  } = options;

  const changes = {};
  const allKeys = new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]);

  allKeys.forEach((key) => {
    if (excludeKeys.includes(key)) return;

    let oldValue = oldObj?.[key];
    let newValue = newObj?.[key];

    // Apply normalizer if provided
    if (normalizeValue && typeof normalizeValue === 'function') {
      oldValue = normalizeValue(oldValue, key);
      newValue = normalizeValue(newValue, key);
    }

    // Semantic null handling
    if (treatSemanticNullAsEqual && isSemanticNull(oldValue) && isSemanticNull(newValue)) {
      if (!includeUnchanged) return;
      changes[key] = {
        old: cloneObject(oldValue),
        new: cloneObject(newValue),
      };
      return;
    }

    const hasChanged = deepCompare ? !isEqual(oldValue, newValue) : oldValue !== newValue;

    if (hasChanged || includeUnchanged) {
      changes[key] = {
        old: cloneObject(oldValue),
        new: cloneObject(newValue),
      };
    }
  });

  return changes;
}

export function validateFee(fee) {
  const { amount, riderPercent, organizationPercent } = fee;
  const emptyFields = [];
  const notAllow = [null, undefined, ''];
  const fields = [
    ['boardingFare', amount],
    ['riderPercent', riderPercent],
    ['organizationPercent', organizationPercent],
  ];

  fields.forEach(([fieldKey, value]) => {
    if (notAllow.includes(value)) emptyFields.push(fieldKey);
  });

  if (emptyFields.length > 0) {
    return { key: 'feeFieldsRequired', emptyFields };
  }

  if (riderPercent > 100 || organizationPercent > 100) return { key: 'percentValuesMustBeBetween0And100' };
  if (Number(riderPercent) + Number(organizationPercent) > 100) return { key: 'totalPercentCannotExceed100' };

  return null;
}

/**
 * Format distance based on length unit (METRIC or IMPERIAL)
 * @param {number} distanceInKm - Distance in kilometers from API
 * @param {string} lengthUnit - Length unit ('METRIC' or 'IMPERIAL')
 * @returns {string} Formatted distance string with unit
 */
export const formatDistance = (distanceInKm, lengthUnit = 'METRIC') => {
  if (!distanceInKm && distanceInKm !== 0) return '-';

  const isImperialUnit = lengthUnit === 'IMPERIAL';
  // Convert km to miles if IMPERIAL (1 km = 0.621371 miles)
  const distanceValue = isImperialUnit ? distanceInKm * 0.621371 : distanceInKm;
  const unit = isImperialUnit ? 'mi' : 'km';

  return `${distanceValue.toFixed(2)} ${unit}`;
};

export function downloadExcelFromResponse(
  response,
  defaultFilename = 'download.xlsx',
  mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
) {
  const contentDisposition = response.headers?.['content-disposition'];
  let filename = defaultFilename;

  if (contentDisposition) {
    // Try to extract filename from Content-Disposition header
    // Supports both: filename="file.xlsx" and filename=file.xlsx
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  createFileFromBinary(response.data, mimeType, filename);
}

export function filterOnEquality(val1, val2) {
  // If filter value is null/undefined, don't filter (show all)
  if (val2 == null) return true;

  // If filter value is an array, check if it's empty or includes val1
  if (Array.isArray(val2)) {
    return val2.length === 0 || val2.includes(val1);
  }

  // If filter value is a string, check if it's empty or equals val1
  if (typeof val2 === 'string') {
    return val2.length === 0 || val1 === val2;
  }

  // For other types, use strict equality
  return val1 === val2;
}

export default objectifyByKey;
